/**
 * 発電形式 (HJKS 準拠 9 区分、conventions.md「発電形式」節) の唯一の SoT。
 *
 * `src/pages/BalanceGeneration`（同時同量発電ダッシュボードと積み上げ順・色編集）で
 * 同じ 9 区分を使うため、`src/pages/<Page>/` 配下ではなくここに置く
 * (`frontend/apps/ebalance/CLAUDE.md`「複数 page で実際に使われてから共有
 * directory へ昇格する」の昇格条件を満たす。2026-09-11 是正: 当初は
 * `pages/Settings/constants/defaults.ts` を BalanceGeneration が参照するだけの
 * 形にしていたが、それは昇格の向きが逆だった)。
 *
 * 積み上げ順 (`DEFAULT_STACK_ORDER`) は
 * `go/internal/backend/domain/valueobject/generation_source_type.go` の
 * `AllGenerationSourceTypes()` godoc が明示するとおり、列挙の内部順とは独立に
 * 決める業務上の表示順。現状は個人設定の既定値をそのまま両画面の既定として
 * 使っているが、conventions.md の 9 区分列挙順 (原子力/水力/火力3区分/地熱/
 * 風力/太陽光/その他) とは並びが異なる。この spec 上の整合は
 * [#7810](https://github.com/enechain/ebalance/issues/7810)（PdM確認）で
 * 追跡中で、本ファイルは現状（個人設定の既定順）を維持したまま実装する。
 */
import { create } from '@bufbuild/protobuf'
import { GenerationSourceType } from '@ebalance/proto/bffuser/v1/balance_model_pb'
import {
  GenerationSourceTypeForSetting,
  type StackOrderItem,
  StackOrderItemSchema,
} from '@ebalance/proto/bffuser/v1/setting_model_pb'

import { LightModeSemanticColors } from '@enechain/ecloud-designsystem'

/**
 * GenSourceKey は発電形式の内訳キー。 `HatsuKomaData` の
 * `Record<GenSourceKey, number | null>` フィールド名・recharts の `dataKey` を
 * 兼ねる。
 */
export type GenSourceKey =
  | 'nuclear'
  | 'coalThermal'
  | 'gasThermal'
  | 'oilThermal'
  | 'geothermal'
  | 'solar'
  | 'wind'
  | 'hydro'
  | 'other'

/**
 * 積み上げ表示順のデフォルト値 (利用者設定の既定値、design/settings.html の
 * DEFAULT_ORDER と対応): Nuclear → ThermalCoal → ThermalGas → ThermalOil →
 * Geothermal → Solar → Wind → Hydro → Other
 */
export const DEFAULT_STACK_ORDER: GenerationSourceTypeForSetting[] = [
  GenerationSourceTypeForSetting.NUCLEAR,
  GenerationSourceTypeForSetting.COAL_THERMAL,
  GenerationSourceTypeForSetting.GAS_THERMAL,
  GenerationSourceTypeForSetting.OIL_THERMAL,
  GenerationSourceTypeForSetting.GEOTHERMAL,
  GenerationSourceTypeForSetting.SOLAR,
  GenerationSourceTypeForSetting.WIND,
  GenerationSourceTypeForSetting.HYDRO,
  GenerationSourceTypeForSetting.OTHER,
]

/**
 * 発電形式のデフォルトカラー。
 *
 * **ここの hex はデザイナー判断で決まった値**であって、 実装の都合で選べる値
 * ではない。 変更する時は判断の出典 (issue 番号) とセットで更新する。
 * 値そのものは `generationSource.test.ts` がリテラルで固定しているので、
 * 出典の無い差し替えは red になる。
 *
 * design 側の既定色は `js/doji-hatsu-prefs.js` / `js/gen-stack-order.js` の
 * `DEFAULT_COLORS_FALLBACK` (= light 値) で、 現在 3 区分が一致しない:
 *
 * | 区分 | 本実装 | design fallback | 経緯 |
 * |---|---|---|---|
 * | その他 | `#9e9e9e` | `#bb97ff` | [#7413](https://github.com/enechain/ebalance/issues/7413) の Des 判断で中立グレーに決めた (理由は当該エントリのコメント)。 design は追随しておらず、 割れの解消は [#4967](https://github.com/enechain/ebalance/issues/4967) が判断待ち |
 * | 地熱 | `#00b0b2` (`Brand.teal`) | `#00a0a3` | design 側は [#4966](https://github.com/enechain/ebalance/issues/4966) の light 是正を取り込み済みだが、 本実装は DS token のまま未取り込み |
 * | 風力 | `#69d582` (`Brand.green`) | `#32a354` | 同上 (出典未特定。 実測で差があることだけが分かっている) |
 *
 * 残り 6 区分 (原子力 `#5d4e00` / 火力 石炭 `#666666`・ガス `#ff1e23`・石油
 * `#870103` / 太陽光 `#ffdd29` / 水力 `#597eff`) は design fallback と一致する。
 */
export const DEFAULT_COLORS: Record<GenerationSourceTypeForSetting, string> = {
  [GenerationSourceTypeForSetting.UNSPECIFIED]: '#cccccc', // eclair token なし (= 未指定のフォールバック)
  [GenerationSourceTypeForSetting.NUCLEAR]:
    LightModeSemanticColors.Text.warningHigh.value,
  [GenerationSourceTypeForSetting.COAL_THERMAL]: '#666666', // eclair ≈ text-mid-emphasis
  [GenerationSourceTypeForSetting.GAS_THERMAL]:
    LightModeSemanticColors.Brand.red.value,
  [GenerationSourceTypeForSetting.OIL_THERMAL]:
    LightModeSemanticColors.Text.negativeHigh.value,
  [GenerationSourceTypeForSetting.GEOTHERMAL]:
    LightModeSemanticColors.Brand.teal.value,
  [GenerationSourceTypeForSetting.SOLAR]:
    LightModeSemanticColors.Brand.yellow.value,
  [GenerationSourceTypeForSetting.WIND]:
    LightModeSemanticColors.Brand.green.value,
  [GenerationSourceTypeForSetting.HYDRO]:
    LightModeSemanticColors.Brand.cyan.value,
  // 「その他」 だけ design/settings.html の `#bb97ff` (紫) を採らない。
  // #7413 の Des 判断: 8080 に「その他」 系列の前例が無いため新規に決めたもので、
  // 要件は (a) 他 8 色と弁別できる (b) **中立**である の 2 点。 その他は
  // 「まだ分類できていない残余」 であって異常でも良好でもないため有彩色は使えず、
  // DS の中立面トークン (`surface-neutral-low` #f8f8f8 / `-mid` #efefef) は
  // 白いカード地に埋もれて棒として読めない。 不透明な中間グレーは
  // `border-high-emphasis` が唯一だったのでこれを採る。
  [GenerationSourceTypeForSetting.OTHER]: '#9e9e9e', // eclair ≈ border-high-emphasis
}

/**
 * デフォルトの StackOrderItem 配列 (= source + color のペア)。
 * BFF に送る際の全フィールド populate、および `buildGenerationSourceList` の
 * 既定引数として使用する。
 */
export const DEFAULT_STACK_ORDER_ITEMS: StackOrderItem[] =
  DEFAULT_STACK_ORDER.map((source) =>
    create(StackOrderItemSchema, {
      source,
      color: DEFAULT_COLORS[source],
    }),
  )

/**
 * 発電形式ラベルの i18n key。 完全な literal 文字列で書く
 * (`scripts/check-i18n-keys.sh` の静的抽出はソース中の namespace 付き quoted
 * literal（`page` または `common` で始まる引用符付き文字列）を正規表現で
 * 拾う仕組みで、テンプレート文字列 (`` `common:generationSource.${key}` ``)
 * は拾えず drift を検出できない — 2026-09-11 是正、旧実装はテンプレート
 * 結合だった)。
 *
 * namespace は 2 画面 (Settings / BalanceGeneration) が共用するため
 * `common:generationSource` 配下（旧: `page:settings.personal.stackOrder`
 * 配下を BalanceGeneration から直参照していたが、他 page の i18n subtree を
 * production code から引く初の diff だったため是正）。
 */
export const SOURCE_TYPE_I18N_KEYS: Record<
  GenerationSourceTypeForSetting,
  string
> = {
  [GenerationSourceTypeForSetting.UNSPECIFIED]: '',
  [GenerationSourceTypeForSetting.COAL_THERMAL]:
    'common:generationSource.coalThermal',
  [GenerationSourceTypeForSetting.GAS_THERMAL]:
    'common:generationSource.gasThermal',
  [GenerationSourceTypeForSetting.OIL_THERMAL]:
    'common:generationSource.oilThermal',
  [GenerationSourceTypeForSetting.NUCLEAR]: 'common:generationSource.nuclear',
  [GenerationSourceTypeForSetting.SOLAR]: 'common:generationSource.solar',
  [GenerationSourceTypeForSetting.WIND]: 'common:generationSource.wind',
  [GenerationSourceTypeForSetting.HYDRO]: 'common:generationSource.hydro',
  [GenerationSourceTypeForSetting.GEOTHERMAL]:
    'common:generationSource.geothermal',
  [GenerationSourceTypeForSetting.OTHER]: 'common:generationSource.other',
}

type GenSourceMeta = {
  sourceType: GenerationSourceType
  key: GenSourceKey
}

/**
 * GenerationSourceTypeForSetting (個人設定側 enum) → GenerationSourceType
 * (balance_model.proto 側 enum) + GenSourceKey の対応表。
 *
 * 2 つの enum は wire 値が数値として一致する設計 (balance_model.proto の
 * `GenerationSourceType` コメント参照) だが、生成される TS 型としては別物
 * なので暗黙 cast はしない (数値一致に依存した cast は片側の enum 変更を
 * tsc で検知できなくなるため、対応表で明示的に写像する)。
 *
 * `Record<GenerationSourceTypeForSetting, GenSourceMeta | undefined>` で
 * **UNSPECIFIED を含む全 enum 値を key として明示**することで、
 * `GenerationSourceTypeForSetting` に 10 個目の値が増えたら tsc が
 * 「このオブジェクトリテラルにキーが足りない」を検出する
 * (2026-09-11 是正: 旧実装は `switch` の `default: throw` で網羅漏れを実行時
 * 例外にしており、module 評価時に throw して import 元の画面全体が
 * 描画不能になっていた。`default` 節が無い分岐だと tsc は網羅性を保証できるが
 * `switch` の `default` はその保証を殺すため、`Record` に切り替えた)。
 */
const SOURCE_META: Record<
  GenerationSourceTypeForSetting,
  GenSourceMeta | undefined
> = {
  [GenerationSourceTypeForSetting.UNSPECIFIED]: undefined,
  [GenerationSourceTypeForSetting.NUCLEAR]: {
    sourceType: GenerationSourceType.NUCLEAR,
    key: 'nuclear',
  },
  [GenerationSourceTypeForSetting.COAL_THERMAL]: {
    sourceType: GenerationSourceType.COAL_THERMAL,
    key: 'coalThermal',
  },
  [GenerationSourceTypeForSetting.GAS_THERMAL]: {
    sourceType: GenerationSourceType.GAS_THERMAL,
    key: 'gasThermal',
  },
  [GenerationSourceTypeForSetting.OIL_THERMAL]: {
    sourceType: GenerationSourceType.OIL_THERMAL,
    key: 'oilThermal',
  },
  [GenerationSourceTypeForSetting.GEOTHERMAL]: {
    sourceType: GenerationSourceType.GEOTHERMAL,
    key: 'geothermal',
  },
  [GenerationSourceTypeForSetting.SOLAR]: {
    sourceType: GenerationSourceType.SOLAR,
    key: 'solar',
  },
  [GenerationSourceTypeForSetting.WIND]: {
    sourceType: GenerationSourceType.WIND,
    key: 'wind',
  },
  [GenerationSourceTypeForSetting.HYDRO]: {
    sourceType: GenerationSourceType.HYDRO,
    key: 'hydro',
  },
  [GenerationSourceTypeForSetting.OTHER]: {
    sourceType: GenerationSourceType.OTHER,
    key: 'other',
  },
}

export type GenSourceItem = {
  sourceType: GenerationSourceType
  key: GenSourceKey
  /** i18n key (`SOURCE_TYPE_I18N_KEYS` 参照、`common:generationSource.*`)。 */
  labelKey: string
  /** 個人設定の色 (`item.color`)。未設定なら `DEFAULT_COLORS` にフォールバック。 */
  color: string
}

/**
 * 1 区分ぶんの `GenSourceItem` を組む。 `source` が 9 区分のいずれでもない
 * (= UNSPECIFIED / 未知値) 時だけ undefined を返す。
 */
function toGenSourceItem(
  source: GenerationSourceTypeForSetting,
  color: string,
): GenSourceItem | undefined {
  const meta = SOURCE_META[source]
  if (meta === undefined) return undefined
  return {
    sourceType: meta.sourceType,
    key: meta.key,
    labelKey: SOURCE_TYPE_I18N_KEYS[source],
    color: color !== '' ? color : DEFAULT_COLORS[source],
  }
}

/**
 * buildGenerationSourceList は発電形式 9 区分の順序付きリストを
 * `StackOrderItem[]`（個人設定の永続値と同じ形）から組み立てる純関数。
 * **返り値は常に 9 区分**で、引数が何であれ欠落も重複も起こさない。
 *
 * 既定引数 `DEFAULT_STACK_ORDER_ITEMS` を省略した呼び出しが現状の唯一の
 * 使い方 (= `GENERATION_SOURCE_LIST`) だが、**個人設定の永続値
 * (`PersonalSettings.stackOrder`) を配線する時はここに実データを渡すだけで
 * 済む形にしてある**（doji-hatsu.md 175-176 / personal-settings.md
 * AC-SPS-007/008。永続値の実配線は本 module の担当外 — 別 issue）。
 *
 * 不整合な入力の扱いは mock-app
 * `GeneratorCategoryDisplayPrefs.OrderedCategories()`
 * (`Models/GeneratorCategory.cs:185-197`) と同じ:
 *
 * - 未知の source（`GenerationSourceTypeForSetting` に無い値）と同一 source の
 *   重複は、警告を出したうえで無視する（1 件の不整合で呼び出し元の画面全体を
 *   描画不能にしない graceful degradation を優先）
 * - **引数を読み終えた後、欠落した区分を `DEFAULT_STACK_ORDER` の順で末尾
 *   (= 積み上げの上段) に補完する**（mock-app の「保存データに欠けている
 *   カテゴリはデフォルト順で末尾（上段）に補完する」）。補完が無いと、永続値
 *   から 1 区分欠けたユーザーの画面でその帯が丸ごと消え、doji-hatsu.md
 *   AC-BDH-010 / AC-BDH-020 NOTE の「9区分の積み上げ合計が発電実績合計と
 *   一致する」が破れる（personal-settings.md AC-SPS-011 の「parse 失敗時は
 *   既定値へフォールバック」とも整合しない）
 */
export function buildGenerationSourceList(
  items: StackOrderItem[] = DEFAULT_STACK_ORDER_ITEMS,
): GenSourceItem[] {
  const seen = new Set<GenSourceKey>()
  const result: GenSourceItem[] = []
  for (const item of items) {
    const entry = toGenSourceItem(item.source, item.color)
    if (entry === undefined) {
      // eslint-disable-next-line no-console -- 開発時に気づけるようにする (画面は落とさない、R-5)
      console.warn(
        `generationSource: unknown GenerationSourceTypeForSetting (${String(item.source)}) — skipping`,
      )
      continue
    }
    if (seen.has(entry.key)) {
      // eslint-disable-next-line no-console -- 同上
      console.warn(
        `generationSource: duplicate source "${entry.key}" in stack order — keeping the first occurrence`,
      )
      continue
    }
    seen.add(entry.key)
    result.push(entry)
  }
  for (const source of DEFAULT_STACK_ORDER) {
    // 色は保存値側にしか無いので、補完分は常に既定色 ('' 指定と同じ経路)。
    const entry = toGenSourceItem(source, '')
    if (entry === undefined || seen.has(entry.key)) continue
    seen.add(entry.key)
    result.push(entry)
  }
  return result
}

/**
 * 発電形式 9 区分の唯一の SoT（既定順・既定色）。badges / toggles / stack /
 * visibility は全てこの 1 本の配列から `.map()` で導出する。
 *
 * `buildGenerationSourceList` が欠落区分を補完するので、この配列は**順序の
 * SoT であると同時に 9 件を持つ**（2026-09-14 是正: 以前は未知 / 重複 source を
 * warn + skip するだけで補完が無く、個人設定の永続値によっては 9 件未満に
 * なりえた）。ただし `Record<GenSourceKey, T>` の seed は依然として
 * `emptyGenSourceRecord` を使う — 理由は同関数の docstring を参照。
 */
export const GENERATION_SOURCE_LIST: readonly GenSourceItem[] =
  buildGenerationSourceList()

/** sourceType (balance_model.proto の wire 値) → GenSourceKey の逆引き。
 *  `convertGenerationResponse.ts` の `breakdownToRecord` が使う。
 *  `SOURCE_META` から作る (`GENERATION_SOURCE_LIST` 由来ではない) ので、
 *  個人設定の永続値が何であれ 9 区分ぶんの写像が揃う。 */
export const GEN_SOURCE_KEY_BY_TYPE: ReadonlyMap<
  GenerationSourceType,
  GenSourceKey
> = new Map(
  Object.values(SOURCE_META)
    .filter((meta): meta is GenSourceMeta => meta !== undefined)
    .map((meta) => [meta.sourceType, meta.key]),
)

/**
 * emptyGenSourceRecord は発電形式 9 区分すべてを key に持つ
 * `Record<GenSourceKey, T>` を、指定した `value` で埋めて返す。
 *
 * **`GENERATION_SOURCE_LIST` ではなく `SOURCE_META` から作る**: 後者は
 * `Record<GenerationSourceTypeForSetting, ...>` として **tsc が全 enum 値の
 * 網羅を強制する**静的リテラルなので、 実行時入力にも他の定数にも依存せず
 * 9 key が揃う。 `GENERATION_SOURCE_LIST` も補完によって実行時には 9 件だが、
 * その 9 件性は `DEFAULT_STACK_ORDER` (ただの配列リテラル = tsc に網羅性を
 * 検査させる形ではない) が全区分を並べていることに依存する。 依存の少ない
 * 方から作ることで、 呼び出し側の `Record<GenSourceKey, T>` という型の主張が
 * 嘘にならない (2026-09-11 導入 / 2026-09-14 理由を再記述)。
 *
 * `Object.fromEntries` は `{ [k: string]: T }` にしか型付けできないため
 * assertion が要る。 呼び出し側 (`DEFAULT_GEN_SERIES_VISIBILITY` /
 * `breakdownToRecord`) では assertion 不要にするため、 ここ 1 箇所に閉じる。
 */
export function emptyGenSourceRecord<T>(value: T): Record<GenSourceKey, T> {
  const entries = Object.values(SOURCE_META)
    .filter((meta): meta is GenSourceMeta => meta !== undefined)
    .map((meta): [GenSourceKey, T] => [meta.key, value])
  return Object.fromEntries(entries) as Record<GenSourceKey, T>
}
